/**
 * Guest Plan Service
 * Handles localStorage-based plan drafts for unauthenticated users
 * and shareable link generation via API
 */

import { apiClient } from './api';
import type { PlanData } from '../types';
import type { ProjectFactsData } from '../components/plan/ProjectFactsStep';
import type { WasteAnalysisData } from '../components/plan/WasteAnalysisStep';
import { safeJsonParse, isPlainObject } from '@/lib/utils';
import { ensureShareLinkResponse, ensureGuestPlanResponse } from '@/lib/api/type-guards';

interface WizardDraftData {
  county?: {
    id: string;
    name: string;
  };
  facts?: ProjectFactsData;
  analysis?: WasteAnalysisData;
}

interface GuestPlanDraft {
  data: Partial<PlanData> | WizardDraftData;
  created_at: string;
  updated_at: string;
}

interface ShareLinkResponse {
  plan_id: string;
  share_url: string;
  share_token: string;
  expires_at: string;
}

/**
 * Create a safe empty PlanData object for use as a fallback
 */
function createEmptyPlanData(): PlanData {
  return {
    project_info: {
      project_name: '',
      project_address: '',
      project_type: 'new_construction',
      estimated_start_date: '',
      estimated_completion_date: '',
    },
    applicant_info: {
      contact_name: '',
      contact_phone: '',
      contact_email: '',
    },
    waste_streams: [],
    compliance: {
      total_waste_generated: 0,
      total_waste_diverted: 0,
      diversion_rate: 0,
      meets_calgreen_requirement: false,
    },
    version: '1.0',
  };
}

export class GuestPlanService {
  private readonly STORAGE_KEY = 'calwmp_guest_plan';

  /**
   * Save plan draft to localStorage
   * Supports both legacy PlanData format and new wizard draft format
   */
  saveDraft(planData: Partial<PlanData> | WizardDraftData): void {
    const draft: GuestPlanDraft = {
      data: planData,
      created_at: this.getDraft()?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(draft));
    } catch (error) {
      console.error('Failed to save guest plan draft:', error);
      // Could be quota exceeded - handle gracefully
    }
  }

  /**
   * Get plan draft from localStorage
   */
  getDraft(): GuestPlanDraft | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) return null;

    const draft = safeJsonParse<GuestPlanDraft | null>(
      stored,
      (value): value is GuestPlanDraft | null => value === null || isPlainObject(value),
      null
    );

    // If parsing failed, return null
    if (draft === null) {
      console.error('Failed to load guest plan draft: invalid JSON');
      return null;
    }

    return draft;
  }

  /**
   * Get just the plan data from draft
   * Note: Returns null if draft is in new wizard format (WizardDraftData)
   */
  getDraftData(): Partial<PlanData> | null {
    const draft = this.getDraft();
    if (!draft?.data) return null;
    // Check if it's the new wizard format - if so, return null
    if ('facts' in draft.data || 'analysis' in draft.data) {
      return null;
    }
    return draft.data as Partial<PlanData>;
  }

  /**
   * Clear plan draft from localStorage
   */
  clearDraft(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear guest plan draft:', error);
    }
  }

  /**
   * Check if a draft exists
   */
  hasDraft(): boolean {
    return this.getDraft() !== null;
  }

  /**
   * Request a shareable link for the plan
   * Creates guest plan on backend and sends email with share link
   */
  async requestShareLink(email: string, planData: PlanData): Promise<ShareLinkResponse> {
    const data = await apiClient.post<unknown>('/plans/guest', {
      email,
      plan_data: planData,
    });

    // Validate response structure
    const response = ensureShareLinkResponse(data);

    // Clear draft after successful link generation
    this.clearDraft();

    return response;
  }

  /**
   * Access a guest plan via share token (no auth required)
   */
  async getGuestPlan(planId: string, shareToken: string): Promise<PlanData> {
    const data = await apiClient.get<unknown>(
      `/plans/${planId}?token=${shareToken}`
    );

    // Validate response structure
    const response = ensureGuestPlanResponse(data);

    // Parse and validate the JSON plan data
    return safeJsonParse<PlanData>(
      response.plan_data,
      (value): value is PlanData => isPlainObject(value),
      createEmptyPlanData()
    );
  }

  /**
   * Update a guest plan via share token (no auth required)
   */
  async updateGuestPlan(
    planId: string,
    shareToken: string,
    planData: Partial<PlanData>
  ): Promise<void> {
    await apiClient.put(`/plans/${planId}?token=${shareToken}`, {
      plan_data: planData,
    });
  }

  /**
   * Convert guest plan to authenticated plan (after user logs in)
   */
  async convertToAuthenticatedPlan(planId: string, shareToken: string): Promise<void> {
    await apiClient.post(`/plans/${planId}/convert`, {
      share_token: shareToken,
    });
  }
}

// Export singleton instance
export const guestPlanService = new GuestPlanService();